import { create } from 'zustand';

/**
 * Sidebar file-manager undo journal. A FIFO of inverse-op records pushed
 * after each *journaled* successful FS op; Cmd-Z scoped to the sidebar pops
 * the most recent and replays it.
 *
 * Deliberately NOT journaled:
 *   - **Trash** — restoring from the OS trash isn't a thing the trash crate
 *     supports cross-platform; users restore via the system Trash UI.
 *   - **Shift-Delete permanent** — by definition irreversible.
 *
 * Each entry stores enough info to invert one op. Stored as a discriminated
 * union so the undo dispatcher can pattern-match.
 */

export type FsUndoEntry =
  | { kind: 'rename'; from: string; to: string }
  | { kind: 'create-file'; path: string }
  | { kind: 'create-folder'; path: string }
  | { kind: 'duplicate'; copyPath: string }
  /** A copy or paste-copy. Inverse: trash the copy. */
  | { kind: 'copy'; copyPath: string }
  /** A move or paste-cut. Inverse: move it back. */
  | { kind: 'move'; from: string; to: string };

/** Soft cap on the journal — older entries are dropped FIFO. */
export const FS_UNDO_CAP = 20;

export interface FsUndoStore {
  entries: readonly FsUndoEntry[];
  /** Push a new inverse-op record; oldest is dropped at the cap. */
  push: (entry: FsUndoEntry) => void;
  /** Remove and return the most recent entry (or null when empty). */
  popLast: () => FsUndoEntry | null;
  /** Wipe the journal — used when workspace roots change or by tests. */
  clear: () => void;
}

export const useFsUndoStore = create<FsUndoStore>((set, get) => ({
  entries: [],
  push: (entry) =>
    set((s) => {
      const next = [...s.entries, entry];
      if (next.length > FS_UNDO_CAP) next.splice(0, next.length - FS_UNDO_CAP);
      return { entries: next };
    }),
  popLast: () => {
    const cur = get().entries;
    if (cur.length === 0) return null;
    const entry = cur[cur.length - 1];
    set({ entries: cur.slice(0, -1) });
    return entry;
  },
  clear: () => set({ entries: [] }),
}));
